import * as React from "react";
import { Tooltip } from "@/components/ui/misc";
import {
  MOS_CEILING, bandDetail, codecText, hasAudioDetail, qualityBand, qualityFigures, qualityFill,
  type QualityBand, type StatTone,
} from "@/lib/call-quality";
import { cn, formatDuration } from "@/lib/utils";
import type { Cdr } from "@/lib/types";

/**
 * How a leg sounded, as a mark in the call report, a panel of figures behind it, and the same
 * figures laid into the call dialog.
 *
 * The switch has always had an opinion about a call's audio and the report never showed it, so a
 * call the lead could barely hear looked exactly like a clean one.
 *
 * **Four rungs on one baseline, not a coloured dot.** The rising silhouette carries the level
 * before the colour does, which is what keeps it readable for a colourblind rep and in a printed
 * report; the colour is the second channel and the score beside it is the third. A dot would put
 * all of it on hue.
 *
 * **A leg with no RTP is drawn as an absence.** A third of the table never carried audio — a leg
 * that never answered, a carrier leg the switch measured nothing on — and an unlit four-rung meter
 * for those would read as the worst call of the day.
 *
 * **Two layouts of one reading.** `CallQualityCard` is the hover: a bordered panel with its own
 * grid, because a hover floats over the table and has to hold its own shape. `CallQualityDetail`
 * is the same score, meter and figures set into a row of the call dialog — no border, no cells,
 * the dialog's own type — because a card dropped into a list of facts reads as a widget that
 * wandered in from another screen. Both take their figures from `qualityFigures`, so they cannot
 * disagree about a number.
 */

const BAND_FILL: Record<QualityBand, string> = {
  excellent: "bg-success-9",
  good: "bg-success-9",
  fair: "bg-warning-9",
  poor: "bg-danger-9",
};

/** Text takes the 11 step, not the 9: the 9s are tuned to be seen as a fill, not read as a word. */
const BAND_TEXT: Record<QualityBand, string> = {
  excellent: "text-success-11",
  good: "text-success-11",
  fair: "text-warning-11",
  poor: "text-danger-11",
};

const BAND_TRACK: Record<QualityBand, string> = {
  excellent: "bg-success-2",
  good: "bg-success-2",
  fair: "bg-warning-2",
  poor: "bg-danger-2",
};

const TONE_TEXT: Record<StatTone, string> = {
  normal: "text-text",
  warn: "text-warning-11",
  bad: "font-medium text-danger-11",
};

/** Ascending rung heights, as a signal meter draws them. */
const RUNG_HEIGHTS = ["h-1.5", "h-2.5", "h-3", "h-3.5"];

function Rungs({ band, className }: { band: QualityBand; className?: string }) {
  const lit = bandDetail(band).rungs;
  return (
    <span className={cn("inline-flex h-3.5 items-end gap-px", className)} aria-hidden>
      {RUNG_HEIGHTS.map((height, i) => (
        <span
          key={height}
          className={cn(
            "w-[3px] rounded-[1px]",
            height,
            i < lit ? BAND_FILL[band] : "bg-border-strong",
          )}
        />
      ))}
    </span>
  );
}

/**
 * The cell in a call row.
 *
 * The score sits beside the rungs rather than only in the hover: a supervisor comparing two rows
 * wants the numbers, and a hover is not available to a reader on a touch screen at all.
 */
export function CallQualityCell({ call }: { call: Cdr }) {
  const band = qualityBand(call.audio_in_mos);

  if (band === null) {
    return (
      <span
        className="inline-flex h-3.5 items-center"
        title="No audio was measured on this leg"
        aria-label="No audio measured"
      >
        <span className="w-3.5 border-t border-dashed border-border-strong" aria-hidden />
      </span>
    );
  }

  const mark = (
    <span
      className="inline-flex items-center gap-1.5"
      aria-label={`Audio ${bandDetail(band).label}, MOS ${call.audio_in_mos!.toFixed(2)}`}
    >
      <Rungs band={band} />
      <span
        className={cn(
          "font-mono text-2xs tabular-nums",
          band === "excellent" || band === "good" ? "text-text-muted" : BAND_TEXT[band],
        )}
      >
        {call.audio_in_mos!.toFixed(2)}
      </span>
    </span>
  );

  if (!hasAudioDetail(call)) return mark;
  return (
    <Tooltip variant="card" side="left" content={<CallQualityCard call={call} />}>
      {/* A span rather than the mark itself: Radix needs one element to attach to, and the
          mark's own layout must not change when it becomes a trigger. */}
      <span className="inline-flex cursor-help">{mark}</span>
    </Tooltip>
  );
}

/** The score against its ceiling and the band it falls in, as one line. */
function Score({ call, band, size }: { call: Cdr; band: QualityBand | null; size: "lg" | "md" }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      {band && size === "md" && <Rungs band={band} className="self-center" />}
      <span
        className={cn(
          "font-mono leading-none font-medium tracking-tight tabular-nums",
          size === "lg" ? "text-xl" : "text-base",
          band && BAND_TEXT[band],
        )}
      >
        {call.audio_in_mos === null ? "—" : call.audio_in_mos.toFixed(2)}
      </span>
      <span className="text-2xs text-text-subtle">/ {MOS_CEILING}</span>
    </span>
  );
}

function BandLabel({ band }: { band: QualityBand | null }) {
  return (
    <span
      className={cn(
        "text-2xs font-semibold uppercase tracking-wide",
        band ? BAND_TEXT[band] : "text-text-subtle",
      )}
    >
      {band ? bandDetail(band).label : "No audio"}
    </span>
  );
}

/** The meter under the score. The track is a light step of the fill's own ramp, so the state
 *  reads across the whole bar and not only across the filled part. */
function Meter({ call, band, fill }: { call: Cdr; band: QualityBand; fill: number }) {
  return (
    <>
      <div className={cn("h-1.5 overflow-hidden rounded-full", BAND_TRACK[band])}>
        <div className={cn("h-full rounded-full", BAND_FILL[band])} style={{ width: `${fill}%` }} />
      </div>
      <div className="mt-1 flex justify-between text-2xs tabular-nums text-text-subtle">
        <span>
          {call.audio_in_quality_percentage === null
            ? `${Math.round(fill)}% of the best this codec reaches`
            : `Quality ${Math.round(fill)}%`}
        </span>
        {call.audio_in_quality_percentage !== null && <span>100%</span>}
      </div>
    </>
  );
}

/**
 * Why it sounded like that: the switch's quality reading as a meter, then the four counts that
 * explain a bad one. Each turns red on its own, so the card names the fault rather than only
 * restating the score.
 */
export function CallQualityCard({ call }: { call: Cdr }) {
  const band = qualityBand(call.audio_in_mos);
  const fill = qualityFill(call);
  const codec = codecText(call.read_codec, call.read_rate);

  return (
    <div className="w-[18.5rem] text-left">
      <div className="border-b border-border px-3.5 py-3">
        <div className="flex items-baseline justify-between gap-2.5">
          <Score call={call} band={band} size="lg" />
          <BandLabel band={band} />
        </div>
        {band !== null && fill !== null && (
          <div className="mt-2">
            <Meter call={call} band={band} fill={fill} />
          </div>
        )}
        {band !== null && <p className="mt-2 text-2xs text-text-muted">{bandDetail(band).said}</p>}
      </div>

      <dl className="grid grid-cols-2">
        {qualityFigures(call).map((figure) => (
          <div key={figure.label} className="border-b border-border px-3.5 py-2 odd:border-r">
            <dt className="text-2xs uppercase tracking-wide text-text-subtle">{figure.label}</dt>
            <dd
              className={cn(
                "mt-0.5 font-mono text-xs tabular-nums",
                figure.value === null ? "text-text-subtle" : TONE_TEXT[figure.tone],
              )}
            >
              {figure.value ?? "—"}
              {figure.hint && (
                <span className="ml-1 text-2xs font-normal text-text-subtle">{figure.hint}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex items-center justify-between gap-2 bg-surface-sunken px-3.5 py-2 font-mono text-2xs text-text-muted">
        <span>{codec ?? "Codec not recorded"}</span>
        <span>{formatDuration(call.billsec_seconds)} talk</span>
      </div>
    </div>
  );
}

/**
 * The same reading set into a row of the call dialog.
 *
 * The score, its band and what a listener would have heard on one line; the meter under it; the
 * figures as label-over-value pairs that wrap with the row, the codec and talk time among them.
 * No border and no cells: it is a fact in a list of facts, and the dialog's dividers are the
 * only lines it needs.
 */
export function CallQualityDetail({ call }: { call: Cdr }) {
  const band = qualityBand(call.audio_in_mos);
  const fill = qualityFill(call);
  const codec = codecText(call.read_codec, call.read_rate);
  const figures: { label: string; value: string | null; hint?: string; tone: StatTone }[] = [
    ...qualityFigures(call),
    { label: "Codec", value: codec, tone: "normal" },
    { label: "Talk", value: formatDuration(call.billsec_seconds), tone: "normal" },
  ];

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Score call={call} band={band} size="md" />
        <BandLabel band={band} />
        {band !== null && <span className="text-xs text-text-muted">{bandDetail(band).said}</span>}
      </div>
      {band !== null && fill !== null && (
        <div className="max-w-sm">
          <Meter call={call} band={band} fill={fill} />
        </div>
      )}
      <dl className="flex flex-wrap gap-x-6 gap-y-2">
        {figures.map((figure) => (
          <div key={figure.label}>
            <dt className="text-2xs uppercase tracking-wide text-text-subtle">{figure.label}</dt>
            <dd
              className={cn(
                "mt-0.5 font-mono text-xs tabular-nums",
                figure.value === null ? "text-text-subtle" : TONE_TEXT[figure.tone],
              )}
            >
              {figure.value ?? "—"}
              {figure.hint && (
                <span className="ml-1 text-2xs font-normal text-text-subtle">{figure.hint}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
